// src/enums.ts
var RunValidationStatus = {
  PENDING: "PENDING",
  VALID: "VALID",
  SUSPICIOUS: "SUSPICIOUS",
  REJECTED: "REJECTED"
};
var OwnerType = { USER: "USER", CLUB: "CLUB" };
var XpEventType = {
  DISTANCE: "DISTANCE",
  TERRITORY_CAPTURE: "TERRITORY_CAPTURE",
  FIRST_DAILY_RUN: "FIRST_DAILY_RUN",
  RUN_COMPLETED: "RUN_COMPLETED",
  SPONSOR_CELL: "SPONSOR_CELL"
};
var GameEventType = {
  RUN_STARTED: "RUN_STARTED",
  RUN_UPDATED: "RUN_UPDATED",
  RUN_FINISHED: "RUN_FINISHED",
  PLAYER_ENTERED_TERRITORY: "PLAYER_ENTERED_TERRITORY",
  TERRITORY_CAPTURED: "TERRITORY_CAPTURED",
  TERRITORY_LOST: "TERRITORY_LOST",
  /** Ownership changed without a capture (a run was deleted): update the map quietly. */
  TERRITORY_UPDATED: "TERRITORY_UPDATED",
  PLAYER_NEARBY: "PLAYER_NEARBY",
  LEADERBOARD_UPDATED: "LEADERBOARD_UPDATED",
  LEVEL_UP: "LEVEL_UP",
  /** A runner reached a new rank tier (level bands). */
  RANK_UP: "RANK_UP",
  /** A runner unlocked a badge tier. */
  BADGE_EARNED: "BADGE_EARNED",
  CLUB_UPDATE: "CLUB_UPDATE",
  RUN_IMPORTED: "RUN_IMPORTED",
  EVENT_UPDATE: "EVENT_UPDATE",
  RUN_COMMENT: "RUN_COMMENT",
  RUN_KUDOS: "RUN_KUDOS",
  COMMENT_REPLY: "COMMENT_REPLY",
  COMMENT_KUDOS: "COMMENT_KUDOS",
  /** The bell: a new in-app notification arrived (or some were read elsewhere). */
  NOTIFICATION: "NOTIFICATION"
};
var LeaderboardType = { XP: "xp", DISTANCE: "distance", TERRITORY: "territory" };
var RunFlag = {
  INVALID_COORDINATES: "INVALID_COORDINATES",
  DUPLICATE_POINTS: "DUPLICATE_POINTS",
  POOR_ACCURACY: "POOR_ACCURACY",
  IMPOSSIBLE_SPEED: "IMPOSSIBLE_SPEED",
  GPS_JUMP: "GPS_JUMP",
  BACKWARD_TIMESTAMP: "BACKWARD_TIMESTAMP",
  FUTURE_TIMESTAMP: "FUTURE_TIMESTAMP",
  TIMESTAMP_OUT_OF_RUN: "TIMESTAMP_OUT_OF_RUN",
  VEHICLE_SPEED: "VEHICLE_SPEED",
  SYNTHETIC_TRACE: "SYNTHETIC_TRACE",
  CLIENT_DISTANCE_MISMATCH: "CLIENT_DISTANCE_MISMATCH",
  TOO_FEW_POINTS: "TOO_FEW_POINTS",
  LOW_QUALITY_TRACE: "LOW_QUALITY_TRACE",
  OUTSIDE_REGION: "OUTSIDE_REGION"
};
var RANK_IDS = ["rookie", "bronze", "silver", "gold", "platinum", "diamond", "master", "legend"];
var BADGE_IDS = [
  "distance",
  "longest_run",
  "streak",
  "runs",
  "captures",
  "held",
  "steals",
  "revenge",
  "explorer",
  "early_bird",
  "night_owl",
  "events_completed",
  "event_podium",
  "team_champion",
  "organizer",
  "club_member"
];

// src/schemas.ts
import { z } from "zod";
var displayNameSchema = z.string().trim().min(2, "Name must be at least 2 characters").max(32).regex(/^[\p{L}\p{N} _.-]+$/u, "Name contains invalid characters");
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").max(128);
var registerSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: passwordSchema,
  displayName: displayNameSchema
});
var changeEmailSchema = z.object({
  email: z.string().trim().toLowerCase().email("Enter a valid email").max(254),
  /** Required when the account has a password. */
  currentPassword: z.string().max(128).optional()
});
var changePasswordSchema = z.object({
  /** Required when the account already has a password. */
  currentPassword: z.string().max(128).optional(),
  newPassword: passwordSchema
});
var loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1).max(128)
});
var gpsPointSchema = z.object({
  /** Monotonic per-run sequence number assigned by the client; used for idempotent upload. */
  seq: z.number().int().min(0),
  /** Tracking segment index; increments after each pause. */
  segment: z.number().int().min(0).max(1e4),
  lat: z.number(),
  lng: z.number(),
  /** Epoch milliseconds from the device. */
  ts: z.number().int(),
  accuracy: z.number().min(0).max(1e5),
  altitude: z.number().nullable().optional(),
  speed: z.number().nullable().optional(),
  heading: z.number().nullable().optional()
});
var createRunSchema = z.object({
  id: z.string().uuid(),
  regionId: z.string().min(1).max(64),
  startedAt: z.number().int()
});
var uploadPointsSchema = z.object({
  points: z.array(gpsPointSchema).min(1).max(2e3)
});
var finishRunSchema = z.object({
  endedAt: z.number().int(),
  /** Client-side distance — informational only, used to detect manipulation. */
  clientDistanceM: z.number().min(0).max(1e6),
  /** Number of points the client recorded; server refuses to finish until it has them all. */
  pointCount: z.number().int().min(0).max(2e5)
});
var paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20)
});
var bboxQuerySchema = z.object({
  minLat: z.coerce.number().min(-90).max(90),
  minLng: z.coerce.number().min(-180).max(180),
  maxLat: z.coerce.number().min(-90).max(90),
  maxLng: z.coerce.number().min(-180).max(180)
});
var clientWsMessageSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("run:progress"),
    runId: z.string().uuid(),
    regionId: z.string(),
    /** Current coarse cell only — precise positions are never broadcast. */
    gridKey: z.string().regex(/^-?\d+:-?\d+$/),
    distanceM: z.number().min(0)
  }),
  z.object({ type: z.literal("ping") }),
  /** Whether the app is on screen; push notifications are skipped while it is. */
  z.object({ type: z.literal("visibility"), visible: z.boolean() })
]);
var notificationPrefsSchema = z.object({ social: z.boolean(), territory: z.boolean(), clubs: z.boolean(), events: z.boolean(), imports: z.boolean(), achievements: z.boolean() }).partial().strict();
var LANGUAGES = ["en", "vi"];
var updateSettingsSchema = z.object({ hideRouteEnds: z.boolean(), notifications: notificationPrefsSchema, language: z.enum(LANGUAGES) }).partial().strict().refine((v) => Object.keys(v).length > 0, "Nothing to update");
var PLAYER_COLORS = [
  "#f43f5e",
  "#f97316",
  "#eab308",
  "#06b6d4",
  "#3b82f6",
  "#8b5cf6",
  "#d946ef",
  "#ec4899",
  "#14b8a6",
  "#a855f7",
  "#ef4444",
  "#0ea5e9"
];
var GENDERS = ["male", "female", "other"];
var optionalText = (max) => z.string().trim().max(max).transform((v) => v === "" ? null : v).nullable();
var updateProfileSchema = z.object({
  displayName: displayNameSchema,
  color: z.enum(PLAYER_COLORS),
  bio: optionalText(150),
  // Health
  gender: z.enum(GENDERS).nullable(),
  birthday: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a date").refine((d) => {
    const t = Date.parse(`${d}T00:00:00Z`);
    return !Number.isNaN(t) && t <= Date.now() && t >= Date.UTC(1900, 0, 1);
  }, "Pick a real birthday").nullable(),
  heightCm: z.number().int("Whole centimetres").min(80, "Height looks wrong").max(250, "Height looks wrong").nullable(),
  weightKg: z.number().min(20, "Weight looks wrong").max(300, "Weight looks wrong").multipleOf(0.1).nullable(),
  // Contact (private)
  country: z.string().regex(/^[A-Z]{2}$/).nullable(),
  address: optionalText(200),
  /** E.164, e.g. +84901234567. */
  phone: z.string().regex(/^\+[1-9]\d{6,14}$/, "Enter a valid phone number").nullable()
}).partial().strict().refine((v) => Object.keys(v).length > 0, "Nothing to update");
var deleteAccountSchema = z.object({ confirm: z.literal("DELETE") }).strict();
var AVATAR_MAX_BYTES = 3e5;
var avatarUploadSchema = z.object({
  image: z.string().max(Math.ceil(AVATAR_MAX_BYTES * 4 / 3) + 40).regex(/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/]+={0,2}$/, "Upload a JPEG, PNG or WebP photo")
});
var createClubSchema = z.object({
  name: z.string().trim().min(3, "Club name must be at least 3 characters").max(40).regex(/^[\p{L}\p{N} _.'&-]+$/u, "Club name contains invalid characters"),
  description: z.string().trim().max(200).default("")
});
var updateClubSchema = createClubSchema.extend({ description: z.string().trim().max(200) }).partial().strict().refine((v) => Object.keys(v).length > 0, "Nothing to update");
var searchQuerySchema = z.object({
  q: z.string().trim().max(60).default(""),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20)
});
var positive = z.number().positive().nullable().optional();
var eventFields = {
  name: z.string().trim().min(3).max(80),
  description: z.string().trim().max(1e3).default(""),
  regionId: z.string().min(1).max(64).default("DA_NANG"),
  /** The cities whose runs count (older clients send regionId only). */
  regionIds: z.array(z.string().min(1).max(64)).min(1).max(50).optional(),
  /** Every city, including cities opened later. */
  allRegions: z.boolean().optional(),
  visibility: z.enum(["PUBLIC", "PRIVATE"]).default("PUBLIC"),
  rule: z.enum(["TERRITORY", "DISTANCE", "XP", "CAPTURES"]),
  teamScoring: z.enum(["TOTAL", "AVERAGE"]).default("TOTAL"),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  minRunDistanceM: positive,
  maxRunDistanceM: positive,
  minPaceSecPerKm: positive,
  maxPaceSecPerKm: positive,
  minTotalDistanceM: positive,
  minRuns: z.number().int().positive().nullable().optional(),
  minSectors: z.number().int().positive().nullable().optional(),
  maxParticipants: z.number().int().min(2).max(1e5).nullable().optional()
};
function eventIssues(e) {
  if (e.startsAt && e.endsAt && e.endsAt.getTime() < e.startsAt.getTime() + 60 * 6e4) return "An event must last at least one hour";
  if (e.minRunDistanceM && e.maxRunDistanceM && e.minRunDistanceM >= e.maxRunDistanceM) return "Minimum run distance must be below the maximum";
  if (e.minPaceSecPerKm && e.maxPaceSecPerKm && e.minPaceSecPerKm >= e.maxPaceSecPerKm) return "Fastest pace must be faster than the slowest pace";
  return null;
}
var createEventSchema = z.object(eventFields).superRefine((e, ctx) => {
  const issue = eventIssues(e);
  if (issue) ctx.addIssue({ code: z.ZodIssueCode.custom, message: issue });
});
var updateEventSchema = z.object(eventFields).partial().refine((s) => Object.keys(s).length > 0, "Nothing to update");
var createTeamSchema = z.object({ name: z.string().trim().min(2).max(40).optional(), clubId: z.string().uuid().optional() }).refine((t) => !!t.name !== !!t.clubId, "Give a team name or pick a club");
var runCommentSchema = z.object({
  body: z.string().trim().min(1, "Write something first").max(500),
  /** Reply to this comment (a reply to a reply joins the same thread). */
  replyTo: z.string().uuid().optional()
});
var joinEventSchema = z.object({ teamId: z.string().uuid().nullable().optional(), code: z.string().max(64).optional() });
var pushSubscriptionSchema = z.object({
  endpoint: z.string().url().max(1e3),
  keys: z.object({ p256dh: z.string().min(16).max(200), auth: z.string().min(8).max(100) })
});
var pushUnsubscribeSchema = z.object({ endpoint: z.string().url().max(1e3) });
var notificationListSchema = z.object({
  before: z.string().datetime().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(30)
});
var notificationReadSchema = z.object({ ids: z.array(z.string().uuid()).max(100).optional() });
var DEFAULT_NOTIFICATION_PREFS = { social: true, territory: true, clubs: true, events: true, imports: true, achievements: true };
var zaloBotTokenSchema = z.object({
  token: z.string().trim().min(10, "Paste the bot token from Zalo Bot Creator").max(300).regex(/^[A-Za-z0-9:_-]+$/, "That does not look like a Zalo bot token")
});
var zaloNotifySchema = z.object({ teams: z.boolean(), joins: z.boolean(), start: z.boolean(), runs: z.boolean(), results: z.boolean() }).partial().strict().refine((v) => Object.keys(v).length > 0, "Nothing to change");
var gameSettingsSchema = z.object({ eventCreationOpen: z.boolean(), freeEventsPerRunner: z.number().int().min(0).max(100) }).partial().refine((v) => Object.keys(v).length > 0, "Nothing to change");
var brandText = (max) => z.string().trim().max(max).nullable().optional();
var brandSchema = z.object({
  name: z.string().trim().min(2).max(80),
  website: z.string().trim().url().max(300).nullable().optional(),
  contactName: brandText(80),
  contactPhone: brandText(30),
  contactEmail: z.string().trim().email().max(120).nullable().optional(),
  notes: z.string().trim().max(2e3).optional(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional()
});
var updateBrandSchema = brandSchema.partial().refine((b) => Object.keys(b).length > 0, "Nothing to update");
var SPONSOR_TIERS = ["DIAMOND", "GOLD", "SILVER", "PARTNER"];
var eventSponsorsSchema = z.object({
  sponsors: z.array(z.object({ brandId: z.string().uuid(), tier: z.enum(SPONSOR_TIERS).default("PARTNER"), note: z.string().trim().max(300).default("") })).max(30).refine((list) => new Set(list.map((s) => s.brandId)).size === list.length, "Each brand only once")
});
var campaignFields = {
  brandId: z.string().uuid(),
  name: z.string().trim().min(2).max(80),
  regionId: z.string().min(1).max(40),
  startsAt: z.coerce.date(),
  endsAt: z.coerce.date(),
  paused: z.boolean().optional(),
  link: z.string().trim().url().max(300).nullable().optional()
};
var campaignSchema = z.object(campaignFields).refine((c) => c.endsAt > c.startsAt, { message: "The end must be after the start", path: ["endsAt"] });
var updateCampaignSchema = z.object(campaignFields).omit({ brandId: true, regionId: true }).partial().refine((c) => Object.keys(c).length > 0, "Nothing to update").refine((c) => !c.startsAt || !c.endsAt || c.endsAt > c.startsAt, { message: "The end must be after the start", path: ["endsAt"] });
var CAMPAIGN_MAX_CELLS = 500;
var campaignCellsSchema = z.object({
  gridKeys: z.array(z.string().regex(/^-?\d+:-?\d+$/)).max(CAMPAIGN_MAX_CELLS).refine((k) => new Set(k).size === k.length, "Each cell only once")
});
var AD_PLACEMENTS = ["CELL", "BANNER"];
var AD_STAT_KINDS = ["VIEW", "CLICK"];
var adStatsSchema = z.object({
  events: z.array(z.object({ campaignId: z.string().uuid(), placement: z.enum(AD_PLACEMENTS), kind: z.enum(AD_STAT_KINDS) })).min(1).max(50)
});
var OFFER_TRIGGERS = ["CAMPAIGN_CELL", "EVENT_FINISH", "EVENT_PODIUM"];
var offerFields = {
  brandId: z.string().uuid(),
  title: z.string().trim().min(2).max(100),
  body: z.string().trim().max(1e3).optional(),
  terms: z.string().trim().max(1e3).optional(),
  trigger: z.enum(OFFER_TRIGGERS),
  campaignId: z.string().uuid().nullable().optional(),
  eventId: z.string().uuid().nullable().optional(),
  expiresAt: z.coerce.date(),
  paused: z.boolean().optional()
};
var offerSchema = z.object(offerFields).refine((o) => o.trigger === "CAMPAIGN_CELL" ? !!o.campaignId : !!o.eventId, { message: "Pick the campaign or event that earns it", path: ["trigger"] });
var updateOfferSchema = z.object(offerFields).pick({ title: true, body: true, terms: true, expiresAt: true, paused: true }).partial().refine((o) => Object.keys(o).length > 0, "Nothing to update");
var OFFER_MAX_CODES = 2e4;
var offerCodesSchema = z.object({ csv: z.string().min(1).max(1e6) });
var SPONSORSHIP_KINDS = ["CASH", "IN_KIND"];
var sponsorshipFields = {
  brandId: z.string().uuid(),
  kind: z.enum(SPONSORSHIP_KINDS),
  amountVnd: z.number().int().min(0).max(1e12).default(0),
  items: z.string().trim().max(1e3).default(""),
  eventId: z.string().uuid().nullable().optional(),
  campaignId: z.string().uuid().nullable().optional(),
  pledgedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  received: z.boolean().default(false),
  note: z.string().trim().max(1e3).default("")
};
var sponsorshipSchema = z.object(sponsorshipFields).refine((s) => s.kind === "CASH" ? s.amountVnd > 0 : s.items.length > 0, { message: "Cash needs an amount; goods need a description", path: ["amountVnd"] });
var updateSponsorshipSchema = z.object(sponsorshipFields).omit({ brandId: true }).partial().refine((s) => Object.keys(s).length > 0, "Nothing to update");
var reportQuerySchema = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional()
});

// src/api.ts
var ROUTE_PRIVACY_ZONE_M = 200;
export {
  AD_PLACEMENTS,
  AD_STAT_KINDS,
  AVATAR_MAX_BYTES,
  BADGE_IDS,
  CAMPAIGN_MAX_CELLS,
  DEFAULT_NOTIFICATION_PREFS,
  GENDERS,
  GameEventType,
  LANGUAGES,
  LeaderboardType,
  OFFER_MAX_CODES,
  OFFER_TRIGGERS,
  OwnerType,
  PLAYER_COLORS,
  RANK_IDS,
  ROUTE_PRIVACY_ZONE_M,
  RunFlag,
  RunValidationStatus,
  SPONSORSHIP_KINDS,
  SPONSOR_TIERS,
  XpEventType,
  adStatsSchema,
  avatarUploadSchema,
  bboxQuerySchema,
  brandSchema,
  campaignCellsSchema,
  campaignSchema,
  changeEmailSchema,
  changePasswordSchema,
  clientWsMessageSchema,
  createClubSchema,
  createEventSchema,
  createRunSchema,
  createTeamSchema,
  deleteAccountSchema,
  eventIssues,
  eventSponsorsSchema,
  finishRunSchema,
  gameSettingsSchema,
  gpsPointSchema,
  joinEventSchema,
  loginSchema,
  notificationListSchema,
  notificationPrefsSchema,
  notificationReadSchema,
  offerCodesSchema,
  offerSchema,
  paginationSchema,
  pushSubscriptionSchema,
  pushUnsubscribeSchema,
  registerSchema,
  reportQuerySchema,
  runCommentSchema,
  searchQuerySchema,
  sponsorshipSchema,
  updateBrandSchema,
  updateCampaignSchema,
  updateClubSchema,
  updateEventSchema,
  updateOfferSchema,
  updateProfileSchema,
  updateSettingsSchema,
  updateSponsorshipSchema,
  uploadPointsSchema,
  zaloBotTokenSchema,
  zaloNotifySchema
};
//# sourceMappingURL=index.js.map